import { RemoteOneDataSource } from "./RemoteOneDataSource";
import {
  pathVariableDtoStrategy,
  pathVariableParentChangeStrategy
} from "./ChildDataSourceStrategies";
import { action, runInAction } from "mobx";
import {
  AnyChildDataSource,
  RemoteChildDataSourceOptions
} from "./RemoteChildDataSource";
import { Lock } from "../helpers/asyncUtils";
import { LoggerFactory } from "../logger";

export class RemoteOneChildDataSource<T, ID> extends RemoteOneDataSource<
  T,
  ID
> {
  public parent: RemoteOneDataSource<any, any>;
  public lock: Lock;
  public readonly urlSuffix: string;
  private readonly dtoStrategy: (ds: AnyChildDataSource, dto: any) => any;
  private readonly parentChangedStrategy: (ds: AnyChildDataSource) => void;

  public constructor(options: RemoteChildDataSourceOptions) {
    super(options);
    this.urlSuffix = options.url;
    this.parent = options.parent;
    this.lock = new Lock(this.name, true);
    this.log = LoggerFactory.create(`RemoteOneChildDataSource [${this.name}]`);
    this.parentChangedStrategy =
      options.parentChangedStrategy || pathVariableParentChangeStrategy();
    this.dtoStrategy = options.dtoStrategy || pathVariableDtoStrategy();
    this.parent.onEvent("one.load", () => this.onParentChanged());
    this.parent.onEvent("one.clear", () => this.onParentChanged());
    this.onParentChanged();
  }

  @action
  public onParentChanged() {
    this.log.debug("onParentChanged", this.parent.id);
    this.parentChangedStrategy(this);
    this.clear();
  }

  @action
  public async findOne(id: ID): Promise<T> {
    await this.lock.waitForUnlock();
    const idString = JSON.stringify(id);
    const parentIdString = JSON.stringify(this.parent.id);

    this.log.debug(
      `find one with parent: ${parentIdString} and id: ${idString}`
    );
    return super.findOne(id);
  }

  @action
  protected findOneSuccess(response: T): T {
    return super.findOneSuccess(this.dtoStrategy(this, response));
  }

  @action
  public async save(): Promise<T> {
    await this.lock.waitForUnlock();
    const parentIdString = JSON.stringify(this.parent.id);

    this.log.debug(`save with parent: ${parentIdString} and url: ${this.url}`);
    runInAction(() => {
      this.one = this.dtoStrategy(this, this.one);
    });
    return super.save();
  }

  @action
  public async update(): Promise<T> {
    await this.lock.waitForUnlock();
    const idString = JSON.stringify(this.id);
    const parentIdString = JSON.stringify(this.parent.id);

    this.log.debug(`update with parent: ${parentIdString} and id: ${idString}`);
    runInAction(() => {
      this.one = this.dtoStrategy(this, this.one);
    });
    return super.update();
  }

  @action
  public async clone(): Promise<T> {
    await this.lock.waitForUnlock();
    const parentIdString = JSON.stringify(this.parent.id);

    this.log.debug(`clone with parent: ${parentIdString} and url: ${this.url}`);

    return super.clone();
  }

  @action
  public async delete(): Promise<void> {
    await this.lock.waitForUnlock();
    const idString = JSON.stringify(this.id);
    const parentIdString = JSON.stringify(this.parent.id);

    this.log.debug(`delete with parent: ${parentIdString} and id: ${idString}`);
    return super.delete();
  }

  @action
  public async download(type: string): Promise<void> {
    await this.lock.waitForUnlock();
    const idString = JSON.stringify(this.id);
    const parentIdString = JSON.stringify(this.parent.id);

    this.log.debug(
      `downloading with parent: ${parentIdString} and id: ${idString} and type: ${type}`
    );
    return super.download(type);
  }

  @action
  public async upload(): Promise<void> {
    await this.lock.waitForUnlock();
    const parentIdString = JSON.stringify(this.parent.id);

    this.log.debug(
      `upload with parent: ${parentIdString} and url: ${this.url}`
    );

    return super.upload();
  }
}
